/**
 * Login Analytics page — summary cards, daily-logins chart, and a paged
 * user table.
 */
import { useCallback, useMemo, useState } from 'react';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import Button from '@/components/Button';
import { useLoginDaily, useLoginSummary, useLoginUserDetail, useLoginUsers } from './api';
import { formatNumber } from './lib/utils';
import { formatDateTime } from '@/lib/utils/dates';
import { useDebouncedSearch, useUrlParams } from './lib/hooks';
import { buildLoginDailyChart } from './lib/charts';
import LoginUserDetailModalBody from './components/LoginUserDetailModalBody';
import { LoaderRow, SummaryCard } from './components/parts';
import type { LoginUsersSortKey, SortOrder } from './types';

const PAGE_SIZE = 50;
const DAY_OPTIONS = [7, 14, 30, 60, 90];
const DEFAULT_DAYS = 30;

const SORTABLE_COLUMNS: { key: LoginUsersSortKey; label: string; sortable: boolean }[] = [
  { key: 'email', label: 'Email', sortable: true },
  { key: 'login_count', label: 'Login Count', sortable: true },
  { key: 'last_login', label: 'Last Login', sortable: true },
  { key: 'first_login', label: 'First Login', sortable: true },
];

export default function LoginAnalyticsPage(): JSX.Element {
  const { params, update } = useUrlParams();

  const page = Math.max(0, parseInt(params.get('page') ?? '0', 10) || 0);
  const search = params.get('q') ?? '';
  const sortBy = (params.get('sort') ?? 'last_login') as LoginUsersSortKey;
  const order = (params.get('order') ?? 'desc') as SortOrder;
  const days = parseInt(params.get('days') ?? String(DEFAULT_DAYS), 10) || DEFAULT_DAYS;

  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

  const onSearchCommit = useCallback(
    (value: string) => update({ q: value || null, page: 0 }),
    [update],
  );
  const searchInput = useDebouncedSearch(search, 300, onSearchCommit);

  const summaryQ = useLoginSummary();
  const dailyQ = useLoginDaily(days);
  const usersQ = useLoginUsers({
    sortBy,
    order,
    search,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const userDetailQ = useLoginUserDetail(selectedEmail);

  const onSort = (key: LoginUsersSortKey): void => {
    if (sortBy === key) {
      update({ order: order === 'desc' ? 'asc' : 'desc', page: 0 });
    } else {
      update({ sort: key, order: 'desc', page: 0 });
    }
  };

  const summary = summaryQ.data;
  const mostActive = summary?.most_active_user
    ? `${summary.most_active_user.split('@')[0]} (${formatNumber(summary.most_active_count)})`
    : '-';

  const { traces, layout } = useMemo(() => buildLoginDailyChart(dailyQ.data ?? []), [dailyQ.data]);

  const total = usersQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6">
      <PageHeader title="Login Analytics" subtitle="User login tracking and activity statistics" />

      <section className="mb-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SummaryCard label="Total Users" value={formatNumber(summary?.total_users)} />
          <SummaryCard label="Total Logins" value={formatNumber(summary?.total_logins)} />
          <SummaryCard label="Logins Today" value={formatNumber(summary?.logins_today)} highlight />
          <SummaryCard label="Users Today" value={formatNumber(summary?.users_today)} />
          <SummaryCard label="Logins This Week" value={formatNumber(summary?.logins_this_week)} />
          <SummaryCard label="Most Active (30d)" value={mostActive} />
        </div>
      </section>

      <Card
        className="mb-8 p-6"
        title="Daily Login Activity"
        actions={
          <select
            className="rounded-md border border-[color:var(--gray-200)] bg-white px-2 py-1 text-sm font-[var(--font-body)] text-[color:var(--text-dark)]"
            value={days}
            onChange={(e) => update({ days: parseInt(e.target.value, 10) })}
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        }
      >
        <div className="mt-4" style={{ height: 320 }}>
          {dailyQ.isError ? (
            <p className="p-8 text-center text-sm text-[color:var(--text-dark)]">
              Failed to load chart data
            </p>
          ) : (
            <PlotlyChart data={traces} layout={layout} />
          )}
        </div>
      </Card>

      <Card
        className="p-6"
        title="User Logins"
        actions={
          <input
            type="text"
            value={searchInput.value}
            onChange={(e) => searchInput.onChange(e.target.value)}
            placeholder="Search by email..."
            className="rounded-md border border-[color:var(--gray-200)] px-3 py-1.5 text-sm font-[var(--font-body)] text-[color:var(--text-dark)]"
          />
        }
      >
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full border-collapse text-sm font-[var(--font-body)]">
            <thead>
              <tr className="bg-[var(--light-bg)]">
                {SORTABLE_COLUMNS.map((col) => {
                  const active = sortBy === col.key;
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={`border-b border-[color:var(--gray-200)] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        active ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-dark)]'
                      } ${col.sortable ? 'cursor-pointer select-none hover:bg-[var(--accent-light)]' : ''}`}
                      onClick={() => {
                        if (col.sortable) onSort(col.key);
                      }}
                    >
                      {col.label}
                      {active ? (order === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </th>
                  );
                })}
                <th className="border-b border-[color:var(--gray-200)] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--text-dark)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {usersQ.isLoading ? (
                <LoaderRow colSpan={5} />
              ) : usersQ.isError ? (
                <LoaderRow colSpan={5} message="Failed to load users" tone="error" />
              ) : usersQ.data && usersQ.data.users.length > 0 ? (
                usersQ.data.users.map((u) => (
                  <tr
                    key={u.email}
                    className="border-b border-[color:var(--gray-200)] hover:bg-[var(--light-bg)]"
                  >
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">{u.email}</td>
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">
                      {formatNumber(u.login_count)}
                    </td>
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">
                      {formatDateTime(u.last_login)}
                    </td>
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">
                      {formatDateTime(u.first_login)}
                    </td>
                    <td className="px-4 py-2">
                      <Button size="sm" variant="primary" onClick={() => setSelectedEmail(u.email)}>
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <LoaderRow colSpan={5} message="No users found" />
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page + 1}
          totalPages={totalPages}
          onChange={(p) => update({ page: Math.max(0, p - 1) })}
        />
      </Card>

      <Modal
        open={selectedEmail !== null}
        onClose={() => setSelectedEmail(null)}
        title={selectedEmail ?? 'User Details'}
        size="lg"
      >
        {userDetailQ.isLoading ? (
          <p className="py-8 text-center text-sm text-[color:var(--text-dark)]">Loading...</p>
        ) : userDetailQ.isError || !userDetailQ.data ? (
          <p className="py-8 text-center text-sm text-red-600">Failed to load user details</p>
        ) : (
          <LoginUserDetailModalBody detail={userDetailQ.data} />
        )}
      </Modal>
    </div>
  );
}
